import { useState, useEffect, useMemo } from 'react';
import {
  PackageCheck,
  Plus,
  Search,
  Loader2,
  X,
  MapPin,
  Calendar,
  Ban,
  Trash2,
  Pencil,
  UserPlus,
  Archive,
  ArchiveRestore,
  Barcode,
  AlertTriangle,
  Upload,
  FileDown,
  Send,
  Mail,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ParcelImportModal from '../components/ParcelImportModal';
import ParcelProfilesTab from '../components/ParcelProfilesTab';
import {
  parcelsApi,
  driversApi,
  devicesApi,
  type Parcel,
  type ParcelStatus,
  type CreateParcelPayload,
} from '../services/api';

const INP =
  'w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500';

const STATUS_COLORS: Record<ParcelStatus, string> = {
  CREATED: 'bg-gray-100 text-gray-700 border-gray-200',
  ASSIGNED: 'bg-blue-100 text-blue-700 border-blue-200',
  OUT_FOR_DELIVERY: 'bg-amber-100 text-amber-800 border-amber-200',
  DELIVERED: 'bg-green-100 text-green-700 border-green-200',
  PARTIALLY_DELIVERED: 'bg-lime-100 text-lime-700 border-lime-200',
  REFUSED: 'bg-red-100 text-red-700 border-red-200',
  ABSENT: 'bg-orange-100 text-orange-700 border-orange-200',
  RETURNED: 'bg-purple-100 text-purple-700 border-purple-200',
  CANCELLED: 'bg-gray-100 text-gray-500 border-gray-200',
};

const ALL_STATUSES: ParcelStatus[] = [
  'CREATED', 'ASSIGNED', 'OUT_FOR_DELIVERY', 'DELIVERED',
  'PARTIALLY_DELIVERED', 'REFUSED', 'ABSENT', 'RETURNED', 'CANCELLED',
];

type FilterStatus = ParcelStatus | 'ALL' | 'ARCHIVED';
type Tab = 'parcels' | 'profiles';

interface SimpleDriver {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  name?: string | null;
  isActive: boolean;
}
interface SimpleDevice {
  id: string;
  name: string;
  plate: string | null;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}
function driverLabel(d: SimpleDriver): string {
  return (d.name ?? `${d.firstName ?? ''} ${d.lastName ?? ''}`).trim() || d.id;
}
function relName(d: { firstName?: string | null; lastName?: string | null } | null): string {
  if (!d) return '';
  return `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim();
}

function haversineKm(a: Parcel, b: Parcel): number {
  const R = 6371;
  const toRad = (n: number) => (n * Math.PI) / 180;
  const dLat = toRad((b.recipientLat ?? 0) - (a.recipientLat ?? 0));
  const dLng = toRad((b.recipientLng ?? 0) - (a.recipientLng ?? 0));
  const lat1 = toRad(a.recipientLat ?? 0);
  const lat2 = toRad(b.recipientLat ?? 0);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * 'date'      → scheduled date ascending (undated last).
 * 'proximity' → group by delivery day, then chain each day by nearest-neighbour
 *   over coordinates so geographically close deliveries sit next to each other
 *   (ready to batch-select for one driver / tour). Parcels without coordinates
 *   fall to the end of their day, grouped by city.
 */
function sortParcels(list: Parcel[], mode: 'date' | 'proximity'): Parcel[] {
  if (mode === 'date') {
    return [...list].sort((x, y) => {
      const dx = x.scheduledDate ? Date.parse(x.scheduledDate) : Infinity;
      const dy = y.scheduledDate ? Date.parse(y.scheduledDate) : Infinity;
      return dx - dy || x.reference.localeCompare(y.reference);
    });
  }
  const dayKey = (p: Parcel) => (p.scheduledDate ? p.scheduledDate.slice(0, 10) : '~~~~');
  const groups = new Map<string, Parcel[]>();
  for (const p of list) {
    const k = dayKey(p);
    const g = groups.get(k);
    if (g) g.push(p);
    else groups.set(k, [p]);
  }
  const out: Parcel[] = [];
  for (const dk of [...groups.keys()].sort()) {
    const arr = groups.get(dk)!;
    const pool = arr.filter((p) => p.recipientLat != null && p.recipientLng != null);
    const without = arr
      .filter((p) => p.recipientLat == null || p.recipientLng == null)
      .sort((a, b) => (a.recipientCity ?? '').localeCompare(b.recipientCity ?? '') || a.reference.localeCompare(b.reference));
    let cur = pool.shift();
    if (cur) out.push(cur);
    while (pool.length && cur) {
      let bi = 0;
      let bd = Infinity;
      for (let i = 0; i < pool.length; i++) {
        const d = haversineKm(cur, pool[i]!);
        if (d < bd) { bd = d; bi = i; }
      }
      cur = pool.splice(bi, 1)[0]!;
      out.push(cur);
    }
    out.push(...without);
  }
  return out;
}

const emptyForm: CreateParcelPayload = {
  orderReference: '',
  barcode: '',
  recipientName: '',
  recipientPhone: '',
  recipientEmail: '',
  recipientAddress: '',
  recipientCity: '',
  recipientPostalCode: '',
  recipientCountry: '',
  senderName: '',
  senderEmail: '',
  senderAddress: '',
  weightKg: null,
  notes: '',
  scheduledDate: null,
};

export default function ParcelsPage() {
  const { t } = useTranslation();

  const [tab, setTab] = useState<Tab>('parcels');

  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState<FilterStatus>('ALL');
  const [filterDriverId, setFilterDriverId] = useState<string>('ALL');
  const [search, setSearch] = useState('');

  const [drivers, setDrivers] = useState<SimpleDriver[]>([]);
  const [devices, setDevices] = useState<SimpleDevice[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<Parcel | null>(null);
  const [form, setForm] = useState<CreateParcelPayload>(emptyForm);
  const [assignDriverId, setAssignDriverId] = useState<string>('');
  const [assignDeviceId, setAssignDeviceId] = useState<string>('');
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);

  // Multi-select + bulk assign + sorting
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortMode, setSortMode] = useState<'date' | 'proximity'>('date');
  const [bulkDriverId, setBulkDriverId] = useState('');
  const [bulkDeviceId, setBulkDeviceId] = useState('');
  const [bulkNotify, setBulkNotify] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const flash = (msg: string) => {
    setBanner(msg);
    setTimeout(() => setBanner(null), 4000);
  };

  const loadParcels = async () => {
    setLoading(true);
    setError(null);
    try {
      const archived = filterStatus === 'ARCHIVED' ? 'archived' : 'active';
      const status = filterStatus === 'ALL' || filterStatus === 'ARCHIVED' ? undefined : filterStatus;
      const data = await parcelsApi.list({ status, archived, search: search || undefined, limit: 300 });
      setParcels(data);
      setSelectedIds(new Set());
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || t('parcels.error_load'));
    } finally {
      setLoading(false);
    }
  };

  const loadSupportData = async () => {
    const [driversRes, devicesRes] = await Promise.allSettled([
      driversApi.list({ isActive: true, limit: 100 }),
      devicesApi.list({ limit: 200 }),
    ]);
    if (driversRes.status === 'fulfilled') {
      const payload =
        driversRes.value.data?.data ||
        (Array.isArray(driversRes.value.data) ? driversRes.value.data : []) || [];
      setDrivers(Array.isArray(payload) ? payload : []);
    }
    if (devicesRes.status === 'fulfilled') {
      const payload =
        devicesRes.value.data?.data ||
        devicesRes.value.data?.devices ||
        (Array.isArray(devicesRes.value.data) ? devicesRes.value.data : []) || [];
      setDevices(Array.isArray(payload) ? payload : []);
    }
  };

  useEffect(() => {
    loadSupportData();
  }, []);

  useEffect(() => {
    if (tab === 'parcels') loadParcels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, tab]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return parcels.filter((p) => {
      // Driver filter: ALL / UNASSIGNED / a specific driver id
      if (filterDriverId === 'UNASSIGNED') {
        if (p.assignedDriverId != null) return false;
      } else if (filterDriverId !== 'ALL') {
        if (p.assignedDriverId !== filterDriverId) return false;
      }
      // Free-text search
      if (q) {
        return (
          p.reference.toLowerCase().includes(q) ||
          (p.barcode?.toLowerCase().includes(q) ?? false) ||
          (p.orderReference?.toLowerCase().includes(q) ?? false) ||
          (p.recipientName?.toLowerCase().includes(q) ?? false) ||
          (p.recipientCity?.toLowerCase().includes(q) ?? false)
        );
      }
      return true;
    });
  }, [parcels, search, filterDriverId]);

  const sorted = useMemo(() => sortParcels(filtered, sortMode), [filtered, sortMode]);

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allSelected = sorted.length > 0 && sorted.every((p) => selectedIds.has(p.id));
  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(sorted.map((p) => p.id)));

  const handleBulkAssign = async () => {
    if (selectedIds.size === 0) return;
    setBulkBusy(true);
    setError(null);
    try {
      const { updated } = await parcelsApi.bulkAssign([...selectedIds], {
        assignedDriverId: bulkDriverId || null,
        assignedDeviceId: bulkDeviceId || null,
        notify: bulkNotify,
      });
      flash(t('parcels.bulk.done', { n: updated }));
      setSelectedIds(new Set());
      setBulkDriverId('');
      setBulkDeviceId('');
      await loadParcels();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || t('parcels.error_save'));
    } finally {
      setBulkBusy(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setAssignDriverId('');
    setAssignDeviceId('');
    setNotify(false);
    setShowForm(true);
  };

  const openEdit = (p: Parcel) => {
    setEditing(p);
    setForm({
      orderReference: p.orderReference ?? '',
      barcode: p.barcode ?? '',
      recipientName: p.recipientName ?? '',
      recipientPhone: p.recipientPhone ?? '',
      recipientEmail: p.recipientEmail ?? '',
      recipientAddress: p.recipientAddress ?? '',
      recipientCity: p.recipientCity ?? '',
      recipientPostalCode: p.recipientPostalCode ?? '',
      recipientCountry: p.recipientCountry ?? '',
      senderName: p.senderName ?? '',
      senderEmail: p.senderEmail ?? '',
      senderAddress: p.senderAddress ?? '',
      weightKg: p.weightKg,
      notes: p.notes ?? '',
      scheduledDate: p.scheduledDate,
    });
    setAssignDriverId(p.assignedDriverId ?? '');
    setAssignDeviceId(p.assignedDeviceId ?? '');
    setNotify(false);
    setShowForm(true);
  };

  const onField = <K extends keyof CreateParcelPayload>(key: K, value: CreateParcelPayload[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Empty text fields must be sent as null, not '' — the backend validates
      // e.g. e-mail format, and '' fails `z.string().email()` (→ 400).
      const norm = (v: string | null | undefined) => (v == null || v === '' ? null : v);
      const payload: CreateParcelPayload = {
        orderReference: norm(form.orderReference),
        barcode: norm(form.barcode),
        recipientName: norm(form.recipientName),
        recipientPhone: norm(form.recipientPhone),
        recipientEmail: norm(form.recipientEmail),
        recipientAddress: norm(form.recipientAddress),
        recipientCity: norm(form.recipientCity),
        recipientPostalCode: norm(form.recipientPostalCode),
        recipientCountry: norm(form.recipientCountry),
        senderName: norm(form.senderName),
        senderEmail: norm(form.senderEmail),
        senderAddress: norm(form.senderAddress),
        notes: norm(form.notes),
        scheduledDate: form.scheduledDate ? new Date(form.scheduledDate).toISOString() : null,
        weightKg: form.weightKg === undefined || form.weightKg === null || Number.isNaN(form.weightKg) ? null : Number(form.weightKg),
      };
      if (editing) {
        await parcelsApi.update(editing.id, payload);
        if ((assignDriverId || null) !== (editing.assignedDriverId || null) ||
            (assignDeviceId || null) !== (editing.assignedDeviceId || null)) {
          await parcelsApi.assign(editing.id, {
            assignedDriverId: assignDriverId || null,
            assignedDeviceId: assignDeviceId || null,
            notify,
          });
        }
        flash(t('parcels.updated'));
      } else {
        await parcelsApi.create({
          ...payload,
          assignedDriverId: assignDriverId || null,
          assignedDeviceId: assignDeviceId || null,
          notify,
        });
        flash(t('parcels.created'));
      }
      setShowForm(false);
      await loadParcels();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || t('parcels.error_save'));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (p: Parcel) => {
    if (!window.confirm(t('parcels.confirm_cancel', { ref: p.reference }))) return;
    try {
      await parcelsApi.cancel(p.id);
      await loadParcels();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || t('parcels.error_save'));
    }
  };

  const handleArchive = async (p: Parcel) => {
    try {
      if (p.archivedAt) await parcelsApi.unarchive(p.id);
      else await parcelsApi.archive(p.id);
      await loadParcels();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || t('parcels.error_save'));
    }
  };

  const handleTracking = async (p: Parcel) => {
    try {
      const { recipient } = await parcelsApi.sendTrackingEmail(p.id);
      flash(t('parcels.track_sent', { email: recipient }));
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || t('parcels.error_save'));
    }
  };

  const handleResend = async (p: Parcel) => {
    try {
      const { recipients } = await parcelsApi.sendPod(p.id);
      flash(recipients.length ? t('parcels.resent', { list: recipients.join(', ') }) : t('parcels.no_email'));
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || t('parcels.error_save'));
    }
  };

  const handleDelete = async (p: Parcel) => {
    if (!window.confirm(t('parcels.confirm_delete', { ref: p.reference }))) return;
    try {
      await parcelsApi.delete(p.id);
      await loadParcels();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || t('parcels.error_save'));
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <PackageCheck className="w-6 h-6 text-primary-600" />
          <h1 className="text-xl font-bold text-gray-900">{t('parcels.title')}</h1>
        </div>
        {tab === 'parcels' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowImport(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium"
            >
              <Upload className="w-4 h-4" />
              {t('parcels.import.title')}
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              {t('parcels.new')}
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
        <TabButton active={tab === 'parcels'} onClick={() => setTab('parcels')}>{t('parcels.tab_parcels')}</TabButton>
        <TabButton active={tab === 'profiles'} onClick={() => setTab('profiles')}>{t('parcels.tab_profiles')}</TabButton>
      </div>

      {banner && (
        <div className="mb-3 px-4 py-2 rounded-lg bg-green-50 text-green-800 text-sm border border-green-200">
          {banner}
        </div>
      )}
      {error && (
        <div className="mb-3 px-4 py-2 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {showImport && (
        <ParcelImportModal onClose={() => setShowImport(false)} onImported={loadParcels} />
      )}

      {tab === 'profiles' ? (
        <ParcelProfilesTab />
      ) : (
        <>
          {/* Filters */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadParcels()}
                placeholder={t('parcels.search_placeholder')}
                className={`${INP} pl-9`}
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="ALL">{t('parcels.filter_all')}</option>
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>{t(`parcels.status.${s}`)}</option>
              ))}
              <option value="ARCHIVED">{t('parcels.filter_archived')}</option>
            </select>
            <select
              value={filterDriverId}
              onChange={(e) => setFilterDriverId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              title={t('parcels.filter_driver_all')}
            >
              <option value="ALL">{t('parcels.filter_driver_all')}</option>
              <option value="UNASSIGNED">{t('parcels.f_unassigned')}</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>{driverLabel(d)}</option>
              ))}
            </select>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as 'date' | 'proximity')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              title={t('parcels.sort_by')}
            >
              <option value="date">{t('parcels.sort_date')}</option>
              <option value="proximity">{t('parcels.sort_proximity')}</option>
            </select>
          </div>

          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 mb-3 p-2 rounded-lg bg-primary-50 border border-primary-200 flex-wrap">
              <span className="text-sm font-medium text-primary-800 px-1">
                {t('parcels.bulk.selected', { n: selectedIds.size })}
              </span>
              <select value={bulkDriverId} onChange={(e) => setBulkDriverId(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
                <option value="">{t('parcels.bulk.pick_driver')}</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{driverLabel(d)}</option>)}
              </select>
              <select value={bulkDeviceId} onChange={(e) => setBulkDeviceId(e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
                <option value="">{t('parcels.bulk.pick_vehicle')}</option>
                {devices.map((d) => <option key={d.id} value={d.id}>{d.name}{d.plate ? ` (${d.plate})` : ''}</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-sm text-gray-700">
                <input type="checkbox" checked={bulkNotify} onChange={(e) => setBulkNotify(e.target.checked)} />
                {t('parcels.f_notify')}
              </label>
              <button
                onClick={handleBulkAssign}
                disabled={bulkBusy || !bulkDriverId}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
              >
                {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                {t('parcels.bulk.assign')}
              </button>
              <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600">
                {t('parcels.bulk.clear')}
              </button>
            </div>
          )}

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <PackageCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>{t('parcels.empty')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2 w-8">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} title={t('parcels.bulk.select_all')} />
                    </th>
                    <th className="text-left px-3 py-2">{t('parcels.col_ref')}</th>
                    <th className="text-left px-3 py-2">{t('parcels.col_recipient')}</th>
                    <th className="text-left px-3 py-2">{t('parcels.col_barcode')}</th>
                    <th className="text-left px-3 py-2">{t('parcels.col_status')}</th>
                    <th className="text-left px-3 py-2">{t('parcels.col_driver')}</th>
                    <th className="text-left px-3 py-2">{t('parcels.col_scheduled')}</th>
                    <th className="text-right px-3 py-2">{t('parcels.col_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sorted.map((p) => {
                    const closed = ['DELIVERED', 'PARTIALLY_DELIVERED', 'REFUSED', 'ABSENT', 'RETURNED', 'CANCELLED'].includes(p.status);
                    return (
                      <tr key={p.id} className={`hover:bg-gray-50 ${selectedIds.has(p.id) ? 'bg-primary-50/40' : ''}`}>
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">
                          {p.reference}
                          {p.orderReference && <div className="text-[10px] text-gray-400">{p.orderReference}</div>}
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">{p.recipientName || '—'}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {[p.recipientPostalCode, p.recipientCity].filter(Boolean).join(' ') || p.recipientAddress || '—'}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          {p.barcode ? (
                            <span className="inline-flex items-center gap-1 font-mono text-xs text-gray-700">
                              <Barcode className="w-3 h-3" /> {p.barcode}
                            </span>
                          ) : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-medium ${STATUS_COLORS[p.status]}`}>
                            {t(`parcels.status.${p.status}`)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-700">{relName(p.assignedDriver) || '—'}</td>
                        <td className="px-3 py-2 text-gray-600 text-xs">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> {fmtDate(p.scheduledDate)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-end gap-1">
                            {!closed && (
                              <button title={t('parcels.edit')} onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-gray-100 text-gray-600">
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            {!closed && (
                              <button title={t('parcels.assign')} onClick={() => openEdit(p)} className="p-1.5 rounded hover:bg-gray-100 text-primary-600">
                                <UserPlus className="w-4 h-4" />
                              </button>
                            )}
                            {!closed && p.recipientEmail && (
                              <button title={t('parcels.track_email')} onClick={() => handleTracking(p)} className="p-1.5 rounded hover:bg-gray-100 text-gray-600">
                                <Mail className="w-4 h-4" />
                              </button>
                            )}
                            {!closed && (
                              <button title={t('parcels.cancel')} onClick={() => handleCancel(p)} className="p-1.5 rounded hover:bg-gray-100 text-amber-600">
                                <Ban className="w-4 h-4" />
                              </button>
                            )}
                            {(p.status === 'DELIVERED' || p.status === 'PARTIALLY_DELIVERED') && (
                              <>
                                <button title={t('parcels.pdf')} onClick={() => parcelsApi.viewPdfInBrowser(p.id)} className="p-1.5 rounded hover:bg-gray-100 text-gray-600">
                                  <FileDown className="w-4 h-4" />
                                </button>
                                <button title={t('parcels.resend')} onClick={() => handleResend(p)} className="p-1.5 rounded hover:bg-gray-100 text-primary-600">
                                  <Send className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            <button title={p.archivedAt ? t('parcels.unarchive') : t('parcels.archive')} onClick={() => handleArchive(p)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500">
                              {p.archivedAt ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                            </button>
                            <button title={t('parcels.delete')} onClick={() => handleDelete(p)} className="p-1.5 rounded hover:bg-gray-100 text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Create / edit slide-over */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30" onClick={() => !saving && setShowForm(false)} />
          <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">{editing ? t('parcels.edit_title', { ref: editing.reference }) : t('parcels.new')}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <Section title={t('parcels.section_parcel')}>
                <Field label={t('parcels.f_order_ref')}>
                  <input className={INP} value={form.orderReference ?? ''} onChange={(e) => onField('orderReference', e.target.value)} />
                </Field>
                <Field label={t('parcels.f_barcode')}>
                  <input className={INP} value={form.barcode ?? ''} onChange={(e) => onField('barcode', e.target.value)} placeholder={t('parcels.f_barcode_ph')} />
                </Field>
                <Field label={t('parcels.f_weight')}>
                  <input type="number" min={0} step="0.1" className={INP} value={form.weightKg ?? ''} onChange={(e) => onField('weightKg', e.target.value === '' ? null : Number(e.target.value))} />
                </Field>
              </Section>

              <Section title={t('parcels.section_recipient')}>
                <Field label={t('parcels.f_name')}>
                  <input className={INP} value={form.recipientName ?? ''} onChange={(e) => onField('recipientName', e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label={t('parcels.f_phone')}>
                    <input className={INP} value={form.recipientPhone ?? ''} onChange={(e) => onField('recipientPhone', e.target.value)} />
                  </Field>
                  <Field label={t('parcels.f_email')}>
                    <input type="email" className={INP} value={form.recipientEmail ?? ''} onChange={(e) => onField('recipientEmail', e.target.value)} />
                  </Field>
                </div>
                <Field label={t('parcels.f_address')}>
                  <input className={INP} value={form.recipientAddress ?? ''} onChange={(e) => onField('recipientAddress', e.target.value)} />
                </Field>
                <div className="grid grid-cols-3 gap-2">
                  <Field label={t('parcels.f_postal')}>
                    <input className={INP} value={form.recipientPostalCode ?? ''} onChange={(e) => onField('recipientPostalCode', e.target.value)} />
                  </Field>
                  <Field label={t('parcels.f_city')}>
                    <input className={INP} value={form.recipientCity ?? ''} onChange={(e) => onField('recipientCity', e.target.value)} />
                  </Field>
                  <Field label={t('parcels.f_country')}>
                    <input className={INP} value={form.recipientCountry ?? ''} onChange={(e) => onField('recipientCountry', e.target.value)} />
                  </Field>
                </div>
              </Section>

              <Section title={t('parcels.section_sender')}>
                <Field label={t('parcels.f_name')}>
                  <input className={INP} value={form.senderName ?? ''} onChange={(e) => onField('senderName', e.target.value)} />
                </Field>
                <Field label={t('parcels.f_email')}>
                  <input type="email" className={INP} value={form.senderEmail ?? ''} onChange={(e) => onField('senderEmail', e.target.value)} />
                </Field>
                <Field label={t('parcels.f_address')}>
                  <input className={INP} value={form.senderAddress ?? ''} onChange={(e) => onField('senderAddress', e.target.value)} />
                </Field>
              </Section>

              <Section title={t('parcels.section_planning')}>
                <Field label={t('parcels.f_scheduled')}>
                  <input
                    type="datetime-local"
                    className={INP}
                    value={form.scheduledDate ? form.scheduledDate.slice(0, 16) : ''}
                    onChange={(e) => onField('scheduledDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                  />
                </Field>
                <Field label={t('parcels.f_driver')}>
                  <select className={INP} value={assignDriverId} onChange={(e) => setAssignDriverId(e.target.value)}>
                    <option value="">{t('parcels.f_unassigned')}</option>
                    {drivers.map((d) => <option key={d.id} value={d.id}>{driverLabel(d)}</option>)}
                  </select>
                </Field>
                <Field label={t('parcels.f_vehicle')}>
                  <select className={INP} value={assignDeviceId} onChange={(e) => setAssignDeviceId(e.target.value)}>
                    <option value="">{t('parcels.f_unassigned')}</option>
                    {devices.map((d) => <option key={d.id} value={d.id}>{d.name}{d.plate ? ` (${d.plate})` : ''}</option>)}
                  </select>
                </Field>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                  {t('parcels.f_notify')}
                </label>
              </Section>

              <Field label={t('parcels.f_notes')}>
                <textarea className={INP} rows={2} value={form.notes ?? ''} onChange={(e) => onField('notes', e.target.value)} />
              </Field>
            </div>

            <div className="flex items-center gap-2 mt-5">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editing ? t('parcels.save') : t('parcels.create')}
              </button>
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">
                {t('parcels.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="border border-gray-200 rounded-lg p-3">
      <legend className="px-1 text-xs font-semibold text-gray-500 uppercase">{title}</legend>
      <div className="space-y-2">{children}</div>
    </fieldset>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
